import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download, LayoutGrid, LayoutList } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ProjectDetails } from '@/types/project';
import type { PaginationInfo, ProjectListItem } from '@/services/projectsApi';
import type { DataMode } from '@/contexts/DataSourceContext';
import { ActiveProjectsGrid } from '@/components/ActiveProjectsGrid';
import { EnhancedProjectCard } from '@/components/projects/EnhancedProjectCard';
import { SkeletonGrid } from '@/components/Pages/ActiveProjects/ProjectSkeletons';
import { ProjectsPagination } from '@/components/Pages/ActiveProjects/ProjectsPagination';
import {
  ProjectsEmptyState,
} from '@/components/Pages/ActiveProjects/ProjectsEmptyState';
import type { EmptyStateType } from '@/components/Pages/ActiveProjects/useActiveProjects';

interface ProjectsViewProps {
  viewMode: 'grid' | 'cards';
  onViewModeChange: (mode: 'grid' | 'cards') => void;
  onExport: () => void;
  isLoading: boolean;
  displayProjects: ProjectDetails[];
  onViewDetails: (project: ProjectDetails | ProjectListItem) => void;
  screenMode: DataMode;
  pagination: PaginationInfo;
  onPageChange: (page: number) => void;
  emptyStateType: EmptyStateType;
  searchQuery: string;
  onClearSearch: () => void;
  onClearAllFilters: () => void;
}

export const ProjectsView: React.FC<ProjectsViewProps> = ({
  viewMode,
  onViewModeChange,
  onExport,
  isLoading,
  displayProjects,
  onViewDetails,
  screenMode,
  pagination,
  onPageChange,
  emptyStateType,
  searchQuery,
  onClearSearch,
  onClearAllFilters,
}) => {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Projects</CardTitle>
            <CardDescription>
              View projects in grid or card format
            </CardDescription>
          </div>
          <div className="flex gap-2">
            {/* Export Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={onExport}
              className="gap-2"
            >
              <Download className="h-4 w-4" />
              Export
            </Button>
            {/* View Mode Icons */}
            <div className="flex gap-1 border rounded-md p-1">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => onViewModeChange('grid')}
                className={cn(
                  "h-7 w-7 p-0",
                  viewMode === 'grid' && "bg-primary text-primary-foreground"
                )}
                title="Grid View"
                aria-label="Switch to grid view"
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'cards' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => onViewModeChange('cards')}
                className={cn(
                  "h-7 w-7 p-0",
                  viewMode === 'cards' && "bg-primary text-primary-foreground"
                )}
                title="Card View"
                aria-label="Switch to card view"
              >
                <LayoutList className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent data-tour="projects-grid">
        {isLoading ? (
          <SkeletonGrid count={6} />
        ) : displayProjects.length === 0 ? (
          <ProjectsEmptyState
            emptyStateType={emptyStateType}
            searchQuery={searchQuery}
            onClearSearch={onClearSearch}
            onClearAllFilters={onClearAllFilters}
          />
        ) : viewMode === 'grid' ? (
          <ActiveProjectsGrid
            projects={displayProjects as ProjectDetails[]}
            onViewDetails={onViewDetails}
          />
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {displayProjects.map((project) => (
              <EnhancedProjectCard
                key={project.id}
                project={project as ProjectDetails}
                onViewDetails={onViewDetails}
              />
            ))}
          </div>
        )}

        {/* Pagination Controls - Only show in live mode when there are multiple pages */}
        {screenMode === 'live' && pagination.totalPages > 1 && (
          <ProjectsPagination
            pagination={pagination}
            isLoading={isLoading}
            onPageChange={onPageChange}
          />
        )}
      </CardContent>
    </Card>
  );
};
